<template>
    <v-col class="pa-0">
        <v-img :src="imageUrl" alt="Header Image" class="header-image d-flex align-center" style="max-height: 200px;">
            <v-row class="title align-center">
                <v-col v-if="competitions.length" class="text-left pl-4">
                    <h1 class="primarytext--text outlined-text ml-4">{{ competitions[0].teamsport.name }}</h1>
                </v-col>
            </v-row>
        </v-img>
        <v-divider></v-divider>
        <div>
            <v-row>
                <v-col cols="auto" sm="auto" class="d-flex justify-start mt-2">
                    <v-chip-group
                        v-model="selectedFilters"
                        multiple
                        column>
                        <v-chip
                            v-for="filter in filterOptions"
                            :key="filter.value"
                            :value="filter.value"
                            :outlined="!selectedFilters.includes(filter.value)"
                            color="primary"
                            label>
                            {{ filter.text }}
                            <v-icon small v-if="selectedFilters.includes(filter.value)">mdi-close</v-icon>
                        </v-chip>
                    </v-chip-group>
                </v-col>
                <!-- Spacer column to push items apart on larger screens -->
                <v-col class="grow d-none d-sm-flex"></v-col>
                <v-col cols="12" sm="auto" class="d-flex justify-end mt-2">
                    <v-select
                        :items="sortingOptions"
                        v-model="selectedSortingOption"
                        :label="$translate('Sort on')"
                        item-text="text"
                        item-value="value"
                        class="mr-2 mt-3"
                        style="max-width: 175px"
                        dense
                    >
                    </v-select>
                    <zoezi-autocomplete
                        v-model="search"
                        :items="computedCompetitions"
                        :search-input.sync="searchText"
                        :label="$translate('Search competition')"
                        item-text="name"
                        item-value="name"
                        return-object
                        prepend-inner-icon="mdi-magnify"
                        style="max-width: 175px"
                        dense
                        class="mt-3"
                        clearable
                    ></zoezi-autocomplete>
                </v-col>
            </v-row>
        </div>
        <div v-if="loading" class="d-flex align-center justify-center">
            <v-progress-circular size="20" indeterminate color="primary" width="2"></v-progress-circular>
        </div>
        <div v-else>
            <v-row v-if="!openAndSoonOpenCompetitions.length">
                <v-col>
                    {{$translate('There are no competitions with open registration')}}
                </v-col>
            </v-row>
            <v-row v-else>
                <v-col
                    v-for="competition in openAndSoonOpenCompetitions"
                    :key="competition.id"
                    cols="12" md="4"
                >
                    <TeamsportCompetitionCard :competition="competition" @showDialog="onShowDialog(competition)"/>
                </v-col>
            </v-row>

            <v-expansion-panels class="zoezi-my-activities" accordion multiple v-model="openPanel">
                <v-expansion-panel>
                    <v-expansion-panel-header>
                        <div style="display: flex; align-items: center;">
                            <strong>{{$translate('Ongoing competitions')}}</strong>
                            <v-badge
                                v-if="ongoingCompetitions.length"
                                color="grey"
                                :content="ongoingCompetitions.length"
                                style="margin-left: 10px;"
                            ></v-badge>
                        </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="z-dense">
                        <v-row v-if="!ongoingCompetitions.length">
                            <v-col>
                                {{$translate('There are no ongoing competitions')}}
                            </v-col>
                        </v-row>
                        <v-row v-else>
                            <v-col
                                v-for="competition in ongoingCompetitions"
                                :key="competition.id"
                                cols="12" md="4"
                            >
                                <TeamsportCompetitionCard :competition="competition" @showDialog="onShowDialog(competition)"/>
                            </v-col>
                        </v-row>
                    </v-expansion-panel-content>
                </v-expansion-panel>

                <!-- Past Competitions Section -->
                <v-expansion-panel>
                    <v-expansion-panel-header>
                        <div style="display: flex; align-items: center;">
                            <strong>{{$translate('Past competitions')}}</strong>
                            <v-badge
                                v-if="pastCompetitions.length"
                                color="grey"
                                :content="pastCompetitions.length"
                                style="margin-left: 10px;"
                            ></v-badge>
                        </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="z-dense">
                        <v-row v-if="!pastCompetitions.length">
                            <v-col>
                                {{$translate('There are no past competitions')}}
                            </v-col>
                        </v-row>
                        <v-row v-else>
                            <v-col
                                v-for="competition in pastCompetitions"
                                :key="competition.id"
                                cols="12" md="4"
                            >
                                <TeamsportCompetitionCard :competition="competition" @showDialog="onShowDialog(competition)" />
                            </v-col>
                        </v-row>
                    </v-expansion-panel-content>
                </v-expansion-panel>

                <!-- Past Competitions previous years -->
                <v-expansion-panel v-if="competitionsPreviousYearsFiltered">
                    <v-expansion-panel-header>
                        <div style="display: flex; align-items: center;">
                            <strong>{{$translate('Past competitions previous years')}}</strong>
                            <v-badge
                                v-if="competitionsPreviousYearsFiltered.length"
                                color="grey"
                                :content="competitionsPreviousYearsFiltered.length"
                                style="margin-left: 10px;"
                            ></v-badge>
                        </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="z-dense">
                        <v-row v-if="!competitionsPreviousYearsFiltered.length">
                            <v-col>
                                {{$translate('There are no competitions from previous years')}}
                            </v-col>
                        </v-row>
                        <v-row v-else>
                            <v-col
                                v-for="competition in competitionsPreviousYearsFiltered"
                                :key="competition.id"
                                cols="12" md="4"
                            >
                                <TeamsportCompetitionCard :competition="competition" @showDialog="onShowDialog(competition)"/>
                            </v-col>
                        </v-row>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>
        </div>
        <v-btn class="mt-2" :loading="loadingPreviousCompetitions" color="primary" outlined v-if="!competitionsPreviousYears.length" @click="getCompetitionsPreviousYears">{{ $translate("Fetch competitions from previous years") }}</v-btn>
        <TeamsportCompetitionDialog v-model="showCompetitionDialog" v-if="showCompetitionDialog && showingCompetition" :competition="showingCompetition" ></TeamsportCompetitionDialog>
    </v-col>
</template>

<script>
import TeamsportCompetitionCard from './TeamsportCompetitionCard.vue';
import TeamsportCompetitionDialog from '../components/TeamsportCompetitionDialog.vue';
import { makeValidPathFromTitle } from '../util.js'
import ZoeziAutocomplete from '../components/ui/form/Autocomplete.vue';

export default {
    name: 'zoezi-teamsport-competition-list',

    zoezi: {
        title: 'Competition list',
        icon: 'mdi-trophy',
        addon: 'TeamSports'
    },

    components: {
        TeamsportCompetitionCard,
        TeamsportCompetitionDialog,
        ZoeziAutocomplete,
    },

    data () {
        return {
            competitions: [],
            loading: false,
            loadingPreviousCompetitions: false,
            search: null,
            searchText: null,
            openPanel:[0],
            teamsports: [],
            category: null,
            showingCompetition: null,
            showCompetitionDialog: false,
            selectedFilters: ['ongoing'],
            competitionsPreviousYears: [],
            selectedSortingOption: 'alphabetical',
        }
    },

    props: {
        categoryId: {
            title: 'Teamsport Category - ID',
            type: Number,
            fulltype: 'select',
            typeinfo: {
                multiple: false,
                text: 'name',
                value: 'id',
                getItems: () => window.$zoeziapi.get('/api/teamsport/get')
            }
        },
        competitionName: {
            title: 'Show competition - Name',
            type: String
        },
        competitionId: {
            title: 'Show competition - ID',
            type: Number
        },
        customFilterOptions: {
            title: 'Filter for game classes',
            type: Array,
            fulltype: 'itemlist',
            default: () => []
        },
    },

    watch: {
        selectedFilters(newFilters) {
            this.updateOpenPanelsFromFilters(newFilters);
        },
        openPanel(newPanels) {
            this.updateFiltersFromOpenPanels(newPanels);
        },
        showCompetitionDialog(show) {
            const pageParams = this.getPageParams();
            if (pageParams.length === 0) {
                return;
            }

            pageParams.forEach(pageParam => {
                const valueToShowOrHide = show ? this.getPageRouteValue(pageParam) : null;
                this.setProp(pageParam.prop, valueToShowOrHide);
            });
        },
        categoryId: {
            immediate: true,
            handler() {
                this.getCompetitions();
            }
        }
    },

    computed: {
        sortingOptions() {
            return [
                { text: this.$translate('Alphabetically'), value: 'alphabetical' },
                { text: this.$translate('Closest competition'), value: 'closest' }
            ];
        },
        defaultFilterOptions() {
            return [
                { text: this.$translate('Registration open'), value: 'open' },
                { text: this.$translate('Ongoing competitions'), value: 'ongoing' },
                { text: this.$translate('Past competitions'), value: 'past' }
            ];
        },
        transformedCustomFilterOptions() {
            return this.customFilterOptions.map(option => {
                return {
                    text: option,
                    value: option.toLowerCase()
                };
            });
        },
        filterOptions() {
            const alwaysIncludeOptions = this.defaultFilterOptions;

            if (!this.customFilterOptions || this.customFilterOptions.length === 0) {
                return this.defaultFilterOptions;
            }

            const filteredOptions = this.defaultFilterOptions
                .slice(0, this.defaultFilterOptions.length - 3)
                .filter(option => this.customFilterOptions.includes(option.value));

            return [...this.transformedCustomFilterOptions, ...filteredOptions, ...alwaysIncludeOptions];
        },
        imageUrl() {
            if (!this.category?.imagekey) return this.$api.getImageUrl('/assets/img/teamsport.jpg')
            return this.$api.getImageUrl(this.category.imagekey)
        },
        today() {
            return Date.newFull(this.$store.state.now30s.clone().yyyymmdd());
        },
        computedCompetitions() {
            const hasGameClassFilter = this.transformedCustomFilterOptions.some(filter => this.selectedFilters.includes(filter.value));
            let competitions = [...this.competitions, ...this.competitionsPreviousYears];

            let computedCompetitions = competitions
                .filter(competition => {
                    if (hasGameClassFilter && competition.gameclasses && competition.gameclasses.length > 0) {
                        return competition.gameclasses.some(gc => {
                            const gameClassName = gc.name.toLowerCase();
                            return this.selectedFilters.includes(gameClassName);
                        });
                    }
                    return true;
                })
                .filter(item =>
                    !this.searchText || item.name.toLowerCase().includes(this.searchText.toLowerCase())
                );

            return computedCompetitions.sort((a, b) => {
                switch (this.selectedSortingOption) {
                    case 'alphabetical':
                        return a.name.localeCompare(b.name);
                    case 'closest':
                        return Date.newFull(a.startDate).getTime() - Date.newFull(b.startDate).getTime();
                }
            });
        },
        ongoingCompetitions() {
            return this.computedCompetitions.filter(competition => {
                let competitionEnd = Date.newFull(competition.endDate);
                let competitionStart = Date.newFull(competition.startDate);
                let registrationEnd = Date.newFull(competition.registrationEndDate);
                return (competitionStart <= this.today && this.today <= competitionEnd) || (registrationEnd < this.today && this.today < competitionStart);
            })
        },
        openAndSoonOpenCompetitions() {
            if(this.selectedFilters.includes('open')) {
                return this.computedCompetitions.filter(competition => {
                    let registrationStart = Date.newFull(competition.registrationStartDate);
                    let registrationEnd = Date.newFull(competition.registrationEndDate);
                    let competitionStart = Date.newFull(competition.startDate);
                    return (registrationStart <= this.today && this.today <= registrationEnd) && (competitionStart > this.today);
                }
                )}
            return this.computedCompetitions.filter(competition => {
                let registrationEnd = Date.newFull(competition.registrationEndDate)
                let competitionStart = Date.newFull(competition.startDate)
                return registrationEnd  >= this.today && competitionStart > this.today;
            })
        },
        pastCompetitions() {
            return this.computedCompetitions.filter(competition => {
                let twelveMonthsAgo = Date.newFull(this.today);
                twelveMonthsAgo.setFullYear(twelveMonthsAgo.getFullYear() - 1);

                let endDate = Date.newFull(competition.endDate);
                return endDate > twelveMonthsAgo && endDate < this.today;
            });
        },
        competitionsPreviousYearsFiltered() {
            return this.computedCompetitions.filter(competition => {
                let twelveMonthsAgo = Date.newFull(this.today);
                twelveMonthsAgo.setFullYear(twelveMonthsAgo.getFullYear() - 1);

                let endDate = Date.newFull(competition.endDate);
                return endDate <= twelveMonthsAgo;
            });
        },
        dateOneYearAgo() {
            var currentDate = this.$store.state.now30s.clone();
            currentDate.setMonth(currentDate.getMonth() - 12);
            var formattedDate = currentDate.toISOString().slice(0, 10);
            return formattedDate;
        },
    },
    mounted() {
        this.getCategory();
        this.$api.subscribe({type: 'teamsportcompetition', callback: () => { this.getCompetitions() } });
    },
    destroyed() {
        this.$api.unsubscribe?.();
    },
    methods: {
        getCategory() {
            this.$api.get('/api/teamsport/get', {id: this.categoryId}).then(teamsport => {
                this.category = teamsport;
            });
        },
        getCompetitionsPreviousYears() {
            this.loadingPreviousCompetitions = true;
            this.$api.get('/api/teamsport/competition/get', {teamsport_id: this.categoryId, only_public: true, full: true, to_date: this.dateOneYearAgo}).then(competitions => {
                this.competitionsPreviousYears = competitions;
                this.loadingPreviousCompetitions = false;
                this.openPanel.push(2);
            });
        },
        getCompetitions() {
            this.loading = true;
            this.$api.get('/api/teamsport/competition/get', {teamsport_id: this.categoryId, only_public: true, full: true, from_date: this.dateOneYearAgo}).then(competitions => {
                this.competitions = competitions;

                if (this.competitionId) {
                    this.showingCompetition = this.competitions.find(x => x.id == this.competitionId);
                } else if (this.competitionName) {
                    let competition = this.competitions.find(x => makeValidPathFromTitle(x.name).substring(1) == this.competitionName);
                    if (competition) {
                        this.showingCompetition = competition;
                    }
                }
                if (this.showingCompetition) {
                    this.showCompetitionDialog = true;
                }
                this.loading = false;
            });
        },
        updateOpenPanelsFromFilters(newFilters) {
            this.openPanel = this.updateFilterAndPanelStates(newFilters, this.openPanel, true);
        },
        updateFiltersFromOpenPanels(newPanels) {
            this.selectedFilters = this.updateFilterAndPanelStates(newPanels, this.selectedFilters, false);
        },
        updateFilterAndPanelStates(sourceArray, targetArray, isSourceFilter = true) {
            const filterPanelMapping = {
                'ongoing': 0,
                'past': 1,
                'previousYears': 2,
            };

            const mapping = isSourceFilter ? filterPanelMapping : Object.fromEntries(Object.entries(filterPanelMapping).map(([k, v]) => [v, k]));

            Object.entries(mapping).forEach(([sourceKey, targetValue]) => {
                const isInSource = sourceArray.includes(isSourceFilter ? sourceKey : Number(sourceKey));
                const isInTarget = targetArray.includes(targetValue);

                if (isInSource && !isInTarget) {
                    targetArray.push(targetValue);
                } else if (!isInSource && isInTarget) {
                    targetArray = targetArray.filter(item => item !== targetValue);
                }
            });

            return targetArray;
        },
        getPageParams() {
            const { style } = this.$parent.config;
            return style?.pageParams?.filter(p => p.prop === 'competitionId' || p.prop === 'competitionName') || [];
        },
        getPageRouteValue(pageParam) {
            if (pageParam.prop === 'competitionId') {
                return this.showingCompetition.id;
            } else if (pageParam.prop === 'competitionName') {
                return makeValidPathFromTitle(this.showingCompetition.name).substring(1);
            }
            return null;
        },
        onShowDialog(competition) {
            this.showingCompetition = competition;
            this.showCompetitionDialog = true;
        }
    },
}
</script>

